import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faClock,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import "./ContattiPage.css";
import { Mail } from "../api/Mail";
import { useToasts } from "../contexts/toasts";
import { TailSpin } from "react-loader-spinner";

const validateEmail = (email: string) => {
  // Regex semplice, puoi raffinare
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validatePhone = (phone: string) => {
  // Accetta numeri italiani, spazi, +, ecc.
  return /^\+?\d{8,15}$/.test(phone.replace(/\s+/g, ""));
};

const ContattiPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailError = formData.email && !validateEmail(formData.email);
  const phoneError = formData.phone && !validatePhone(formData.phone);

  const { showToast } = useToasts();

  const isFormValid = useMemo(() => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.phone.trim() !== "" &&
      validateEmail(formData.email) &&
      validatePhone(formData.phone)
    );
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    setIsSubmitting(true);
    try {
      await Mail.sendEmail({
        email: formData.email,
        fullName: formData.name,
        phoneNumber: formData.phone,
        message: formData.message,
      });
      showToast(
        "mail-success",
        "Grazie per averci contattato! Ti risponderemo al più presto.",
        "Messaggio inviato",
        "success",
      );
      //setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      showToast(
        "mail-error",
        "Errore nell'invio del messaggio. Riprova più tardi.",
        "",
        "error",
      );
    }
    setIsSubmitting(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="contatti-page">
      {/* Header Section */}
      <section className="contatti-header">
        <div className="contatti-header-overlay">
          <div className="contatti-header-container">
            <p className="contatti-header-label">CONTATTACI</p>
            <h1 className="contatti-header-title">
              Prenota la Tua Visita Oggi
            </h1>
            <p className="contatti-header-description">
              Il primo passo verso la salute è una visita specialistica.
              Contattaci per fissare un appuntamento o per qualsiasi
              informazione.
            </p>

            <div className="contatti-header-buttons">
              <a href="#form" className="btn-header-primary">
                <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                Prenota Online
              </a>
              <a href="tel:+393701627395" className="btn-header-secondary">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                Chiama Ora
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="contatti-info">
        <div className="contatti-info-container">
          <div className="info-card">
            <div className="info-card-icon">
              <FontAwesomeIcon icon={faPhone} />
            </div>
            <h3 className="info-card-title">Telefono</h3>
            <p className="info-card-value phone-number">+39 370 162 7395</p>
            <p className="info-card-value phone-number">+39 091 766 9253</p>
            <div>
              <a
                href="tel:+393701627395"
                className="info-card-link"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                Chiama cellulare →
              </a>
              <a
                href="tel:+390917669253"
                className="info-card-link"
                style={{ display: "block" }}
              >
                Chiama fisso →
              </a>
            </div>
          </div>

          <div className="info-card">
            <div className="info-card-icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <h3 className="info-card-title">Email</h3>
            <p className="info-card-value">centrodiotorino.mattina@gmail.com</p>
            <a
              href="mailto:centrodiotorino.mattina@gmail.com"
              className="info-card-link"
            >
              Scrivi email →
            </a>
          </div>

          <div className="info-card">
            <div className="info-card-icon">
              <FontAwesomeIcon icon={faLocationDot} />
            </div>
            <h3 className="info-card-title">Studio</h3>
            <p className="info-card-value">
              Via Capo dell'Acqua 6 (Piazza Duomo), Partinico
            </p>
            <a
              href="https://maps.google.com/?q=Via+Capo+dell'Acqua+6,+Piazza+Duomo,+Partinico"
              target="_blank"
              rel="noopener noreferrer"
              className="info-card-link"
            >
              Vedi mappa →
            </a>
          </div>
        </div>
      </section>

      {/* Orari Section */}
      <section className="contatti-orari">
        <div className="contatti-orari-container">
          <div className="orari-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <h2 className="orari-title">Orari di Apertura</h2>

          <div className="orari-list">
            <div className="orario-item">
              <span className="orario-day">Lunedì</span>
              <span className="orario-hours">09:00 - 13:00</span>
            </div>
            <div className="orario-item">
              <span className="orario-day">Mercoledì</span>
              <span className="orario-hours">
                09:00 - 13:00 / 15:00 - 19:00
              </span>
            </div>
            <div className="orario-item">
              <span className="orario-day">Venerdì</span>
              <span className="orario-hours">
                09:00 - 13:00 / 15:00 - 19:00
              </span>
            </div>
            <div className="orario-item">
              <span className="orario-day">Altri giorni</span>
              <span className="orario-hours">Chiuso</span>
            </div>
          </div>

          <p className="orari-note">
            * Gli appuntamenti sono su prenotazione. Contattaci per verificare
            la disponibilità.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contatti-form-section" id="form">
        <div className="contatti-form-container">
          <h2 className="form-title">Invia un Messaggio</h2>
          <p className="form-description">
            Compila il form e ti ricontatteremo al più presto per fissare un
            appuntamento o rispondere alle tue domande.
          </p>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nome e Cognome *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Mario Rossi"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefono *</label>
                <input
                  type="tel"
                  id="phone"
                  inputMode="numeric"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+39 370 162 7395"
                />
                {phoneError && (
                  <div style={{ color: "#e74c3c", fontSize: 13, marginTop: 4 }}>
                    Inserisci un numero di telefono valido
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="mario.rossi@email.com"
              />
              {emailError && (
                <div style={{ color: "#e74c3c", fontSize: 13, marginTop: 4 }}>
                  Inserisci un indirizzo email valido
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Messaggio</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                placeholder="Descrivi brevemente il motivo della richiesta di visita..."
              ></textarea>
            </div>

            <button
              type="submit"
              className="form-submit-btn"
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? (
                <TailSpin height="20" width="20" color="#fff" />
              ) : (
                "Invia Messaggio"
              )}
            </button>

            <p className="form-privacy">
              Inviando questo form accetti la nostra{" "}
              <a href="/privacy">Privacy Policy</a>.
            </p>
          </form>
        </div>
      </section>

      {/* Map Section */}
      <section className="contatti-map" id="map">
        <div className="map-embed-container" style={{ width: "100%", maxWidth: 700, margin: "0 auto", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.08)" }}>
          <iframe
            title="Mappa Centro Mattina"
            src="https://www.google.com/maps?q=Via+Capo+dell'Acqua+6,+Piazza+Duomo,+Partinico&output=embed"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default ContattiPage;
